import axiosInstance from "../api/authAPI";
import { LogoutResponse } from "../app/authSlice";
import { FetchMemberResponse } from "../types/surveyTypes";
import {
  ApiStatusResponse,
  FetchUserParams,
  UserApiResponse,
  UserResponse,
} from "../types/taskTypes";
interface UserDropDown {
  id: number; // use `key` from API as `id` here
  name: string;
}
//login user
export const login = async (
  email: string,
  password: string,
  is_web?: number
) => {
  try {
    const response = await axiosInstance.post("/user/login", {
      email,
      password,
      is_web,
    });
    return response.data; // Assuming API returns { token, user }
  } catch (error: any) {
    throw error.response?.data || "Login failed";
  }
};

// logout user
export const logout = async () => {
  try {
    const response = await axiosInstance.post("/user/logout"); // no payload
    return response.data; // { status, status_code, message }
  } catch (error: any) {
    throw error.response?.data || "Logout failed";
  }
};

// Create User
export const createUser = async (user: FormData) => {
  try {
    const response = await axiosInstance.post("/users", user, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  } catch (error: any) {
    throw error.response?.data?.message || "User creation failed";
  }
};

// Update User
export const updateUser = async (
  id: number,
  userData: FormData
): Promise<ApiStatusResponse> => {
  try {
    const response = await axiosInstance.post<ApiStatusResponse>(
      `/users/${id}`,
      userData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  } catch (error: any) {
    throw error.response?.data?.message || "User update failed";
  }
};

export const fetchUserList = async (
  params: FetchUserParams
): Promise<UserApiResponse> => {
  try {
    const response = await axiosInstance.get("/users", { params });
    return response.data;
  } catch (error: any) {
    throw error.response?.data || { message: "Failed to fetch users" };
  }
};

// Delete User
export const deleteUser = async (id: number): Promise<ApiStatusResponse> => {
  try {
    const response = await axiosInstance.delete(`/users/${id}`);
    return response.data;
  } catch (error: any) {
    throw error.response?.data || { message: "User deletion failed" };
  }
};

// get specific User
export const getUserDetail = async (id: number): Promise<ApiStatusResponse> => {
  try {
    const response = await axiosInstance.get(`/users/${id}`);
    return response.data;
  } catch (error: any) {
    throw error.response?.data || { message: "User deletion failed" };
  }
};

// get user detail
export const fetchUserProfile = async (): Promise<UserResponse> => {
  try {
    const response = await axiosInstance.get("/user/me"); // GET {{URL}}/api/{{Version}}/user/me
    return response.data.data; // Return the actual user data (nested under `.data`)
  } catch (error: any) {
    throw error.response?.data || { message: "Failed to fetch user profile" };
  }
};

// forgot-password
export const forgotPasswordAPI = async (email: string) => {
  try {
    const response = await axiosInstance.post("/user/forgot-password", {
      email,
    });
    return response.data;
  } catch (error: any) {
    throw error.response?.data || { message: "Something went wrong" };
  }
};

// Reset Password
export const resetPasswordAPI = async (payload: {
  email: string;
  password: string;
  token: string;
}): Promise<LogoutResponse> => {
  try {
    const response = await axiosInstance.post("/user/reset-password", payload);
    return response.data;
  } catch (error: any) {
    throw error.response?.data || { message: "Something went wrong" };
  }
};

// project dropdown list
export const fetchUserDropdownList = async (): Promise<UserDropDown[]> => {
  const response = await axiosInstance.get("/users-list");
  const data = response.data;

  if (data.status) {
    // Convert `key` to `id` so your dropdown uses `project.id`
    return data.data.map((user: any) => ({
      id: user.key,
      name: user.name,
    }));
  } else {
    throw new Error(data.message || "Failed to fetch user list");
  }
};

// dashboard api
export const fetchDashboard = async (is_admin: number) => {
  try {
    const response = await axiosInstance.get("/users-dashboard", {
      params: { is_admin },
    });
    return response.data; // contains status, message, data
  } catch (error: any) {
    throw error.response?.data || "Failed to fetch dashboard";
  }
};

export const fetchUserSurveys = async (
  userId: number,
  is_admin: number,
  date_filter?: "last_week" | "last_month" | "custom",
  start_date?: string,
  end_date?: string
): Promise<any> => {
  try {
    const params: Record<string, any> = { is_admin };
    if (date_filter) params.date_filter = date_filter;
    if (start_date) params.start_date = start_date;
    if (end_date) params.end_date = end_date;
    const response = await axiosInstance.get(`/users/${userId}/surveys`, {
      params,
    });
    return response.data; // expected to return { status, message, data }
  } catch (error: any) {
    throw error.response?.data || { message: "Failed to fetch user surveys" };
  }
};
interface FetchMemberParams {
  search?: string;
  reporting_manager_id?: number;
  page?: number;
  pagesize?: number;
  survey_id?: number;
}

export const fetchMemberList = async (
  params: FetchMemberParams
): Promise<FetchMemberResponse> => {
  try {
    const response = await axiosInstance.get<FetchMemberResponse>(
      "/get-members",
      {
        params: {
          search: params.search ?? "",
          reporting_manager_id: params.reporting_manager_id ?? "",
          page: params.page ?? 1,
          pagesize: params.pagesize ?? 50,
          survey_id: params.survey_id ?? "",
        },
      }
    );
    return response.data;
  } catch (error: any) {
    throw error.response?.data || { message: "Failed to fetch member list" };
  }
};
